import React, { useEffect, useState } from 'react';
import * as PropTypes from 'prop-types';
import { requests } from '../../lib/helper/agent';
import { isStaff } from '../../lib/helper/authorisation';
import { EMAIL_ATTACHMENTS, EMAIL_ATTACHMENT_DOWNLOAD } from '../../lib/routing/api';

const formatBytes = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const isPastedImage = (attachment) => attachment.isInline && (attachment.mimeType || '').startsWith('image/');

const InlineImageThumbnail = ({ attachment }) => {
  const [blobUrl, setBlobUrl] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;

    requests
      .getBlobUrl(EMAIL_ATTACHMENT_DOWNLOAD.replace(':id', attachment.id))
      .then((url) => {
        if (cancelled) return;
        objectUrl = url;
        setBlobUrl(url);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [attachment.id]);

  if (!blobUrl) return null;

  return (
    <a href={blobUrl} target="_blank" rel="noopener noreferrer" className="d-inline-block mr-2 mb-2">
      <img
        src={blobUrl}
        alt={attachment.originalFilename}
        className="border rounded"
        style={{ maxWidth: 160, maxHeight: 160, objectFit: 'contain' }}
      />
    </a>
  );
};

InlineImageThumbnail.propTypes = {
  attachment: PropTypes.shape({
    id: PropTypes.number.isRequired,
    originalFilename: PropTypes.string,
  }).isRequired,
};

const EmailAttachments = ({ attachableType, attachableId, staffOnly = true }) => {
  const [attachments, setAttachments] = useState([]);

  useEffect(() => {
    if (!attachableId) return;
    requests
      // Inline attachments must be included — they are the pasted images rendered
      // as thumbnails below. Filtering them out here silently disables that.
      .get(EMAIL_ATTACHMENTS, { attachableType, attachableId })
      .then((body) => setAttachments(body['hydra:member'] ?? []))
      .catch(() => {});
  }, [attachableType, attachableId]);

  if ((staffOnly && !isStaff()) || attachments.length === 0) return null;

  const pastedImages = attachments.filter(isPastedImage);
  const otherAttachments = attachments.filter((attachment) => !isPastedImage(attachment));

  return (
    <div className="email-attachments mt-2">
      {pastedImages.length > 0 && (
        <div className="mb-2">
          <p className="mb-1 small font-weight-bold text-muted">
            <i className="fas fa-image mr-1" />
            Pasted images ({pastedImages.length})
          </p>
          <div className="d-flex flex-wrap">
            {pastedImages.map((attachment) => (
              <InlineImageThumbnail key={attachment.id} attachment={attachment} />
            ))}
          </div>
        </div>
      )}

      {otherAttachments.length > 0 && (
        <>
          <p className="mb-1 small font-weight-bold text-muted">
            <i className="fas fa-paperclip mr-1" />
            Attachments ({otherAttachments.length})
          </p>
          <ul className="list-unstyled mb-0">
            {otherAttachments.map((attachment) => (
              <li key={attachment.id} className="small">
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0 text-left w-auto"
                  onClick={() =>
                    requests.getFile(
                      EMAIL_ATTACHMENT_DOWNLOAD.replace(':id', attachment.id),
                      {},
                      attachment.originalFilename,
                    )
                  }>
                  <i className="fas fa-download mr-1" />
                  {attachment.originalFilename} <span className="text-muted ml-1">({formatBytes(attachment.size)})</span>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
};

EmailAttachments.propTypes = {
  attachableType: PropTypes.string.isRequired,
  attachableId: PropTypes.number,
  staffOnly: PropTypes.bool,
};

export default EmailAttachments;
